import { Dispatch, SetStateAction } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ISaveComponent } from "../../interfaces/services";
import { EtyComponent } from "../../interfaces/entities";
import { IComponentForTree, ISelectedData } from "../../interfaces/tree";

import useToast from "../useToast";

import { saveComponent } from "../../services/components";

export interface IPayload {
    payload: ISaveComponent;
    setIsProcessing: Dispatch<SetStateAction<boolean>>;
    selected: ISelectedData | null;
    setSelected: Dispatch<SetStateAction<ISelectedData | null>>;
};

export interface IResponse {
    error?: string;
    message: string;
    statusCode: number;
    data: EtyComponent;
};

const useSaveComponent = () => {
    const queryClient = useQueryClient();

    const { addToast } = useToast();

    const mutationFn = async (data: IPayload): Promise<IResponse> => {
        return await saveComponent(data.payload);
    };

    return useMutation({
        mutationFn,
        onSuccess: (response: IResponse, variables: IPayload) => {
            if (response?.statusCode === 200) {
                addToast({
                    type: 'success',
                    title: 'Component saved successfully.',
                });

                if (variables.selected) {
                    variables.setSelected({
                        id: response.data.id,
                        level: 1,
                        data: {
                            ...(variables.selected.data as IComponentForTree),
                            id: response.data.id,
                            program_id: response.data.program_id,
                            description: response.data.description,
                            method: response.data.method,
                        }
                    });
                };

                queryClient.invalidateQueries({ queryKey: ['programs_tree'] });
            };
        },
        onError: (error: IResponse, variables: IPayload) => {
            addToast({
                type: 'error',
                title: 'Error occured',
                message: error.message
            });
        },
        onSettled: (data: IResponse | undefined, error: IResponse | null, variables: IPayload) => {
            variables.setIsProcessing(false);
        },
    });
};

export default useSaveComponent;